import React, { useEffect, useRef, useState } from 'react';
import Appear from '../../components/ui/Appear';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import { IconButton, buttonClass } from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import { AuthorityChip, AuthorityPicker } from '../Authority';
import { DANGER_ROW_BUTTON, ICON_PATH, NODE_SWATCH, NodeEditor, ROW_BUTTON, useReturnFocus } from '../DocSection';
import PointsPanel from '../PointsPanel';
import type { PointsHost } from '../pointsTypes';
import type { NodeColor, NodePatch, PlanMember, PlanNode } from '../types';

// The document view's picker is private to DocSection; these mirror its order and names.
const COLORS: NodeColor[] = ['none', 'gold', 'sky', 'moss', 'plum', 'rust', 'slate'];
const COLOR_NAME: Record<NodeColor, string> = {
  none: 'No color',
  gold: 'Gold',
  sky: 'Sky',
  moss: 'Moss',
  plum: 'Plum',
  rust: 'Rust',
  slate: 'Slate',
};

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';
// Transparent with padding; the state layer is the only hover. The ring shows for keyboard focus only.

const ACCEPT = buttonClass('filled', 'sm');

export interface NodeSidePanelProps {
  node: PlanNode;
  /** The section number in flow order, '3', for an accepted node. */
  number?: string;
  /** The group this node sits in, when it sits in one. */
  parentTitle?: string;
  /** Opens that group in this panel. */
  onOpenParent?: () => void;
  /** Color and removal: a manager, or this node's authority, on a writable plan. */
  canEditNode: boolean;
  /** Accept and reject a proposal: a manager on a writable plan. */
  canDecide: boolean;
  /** May edit the title and notes: anyone above, or the member who proposed it. */
  canEditText: boolean;
  /** Set who holds this node: a manager on a writable plan. */
  canSetAuthority: boolean;
  members: PlanMember[];
  /** Who is reading and what the plan allows, for the points under this topic. */
  points: PointsHost;
  onSetAuthority: (userId: string | null) => Promise<boolean>;
  /** Bumped when the panel should take keyboard focus (Enter on a node). */
  focusToken: number;
  onSave: (patch: NodePatch) => Promise<boolean>;
  onAccept: () => Promise<boolean>;
  onReject: () => Promise<void>;
  /** Marks the topic done, or reopens it. */
  onToggleDone: () => Promise<boolean>;
  /** Asks for confirmation itself. */
  onDelete: () => Promise<void>;
  onOpenInDocument: () => void;
  onClose: () => void;
}

const MIN_PANEL_WIDTH = 360;
const MAX_PANEL_WIDTH = 720;
const DEFAULT_PANEL_WIDTH = 480;
const PANEL_WIDTH_KEY = 'node-plan:panel-width';
const clampWidth = (w: number) => Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, Math.round(w)));

function usePanelWidth(): [number, (w: number) => void] {
  const [width, setWidth] = useState<number>(() => {
    try {
      const saved = Number(window.localStorage.getItem(PANEL_WIDTH_KEY));
      return saved ? clampWidth(saved) : DEFAULT_PANEL_WIDTH;
    } catch {
      return DEFAULT_PANEL_WIDTH;
    }
  });
  const set = (w: number) => {
    setWidth(w);
    try { window.localStorage.setItem(PANEL_WIDTH_KEY, String(w)); } catch { /* private mode */ }
  };
  return [width, set];
}

/** True at 760px and up, where the panel floats instead of filling the screen. */
function useWide(): boolean {
  const query = '(min-width: 760px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = () => setWide(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return wide;
}

const NodeSidePanel: React.FC<NodeSidePanelProps> = ({
  node,
  number,
  parentTitle,
  onOpenParent,
  canEditNode,
  canDecide,
  canEditText,
  canSetAuthority,
  members,
  points,
  onSetAuthority,
  focusToken,
  onSave,
  onAccept,
  onReject,
  onToggleDone,
  onDelete,
  onOpenInDocument,
  onClose,
}) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const editTrigger = useReturnFocus<HTMLButtonElement>(editing);
  const authorityTrigger = useReturnFocus<HTMLButtonElement>(choosing);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const proposed = node.status === 'proposed';
  const done = !!node.done_at;
  const canMarkDone = canEditNode && !proposed && node.kind === 'topic';
  const headingId = `np-panel-${node.id}`;

  // A different node, or lost rights, closes the editor.
  useEffect(() => {
    setEditing(false);
  }, [node.id, canEditText]);

  useEffect(() => {
    setChoosing(false);
  }, [node.id, canSetAuthority]);

  useEffect(() => {
    if (focusToken > 0) headingRef.current?.focus();
  }, [focusToken]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };


  // A conversation can happen in here, so the panel is wide by default and the
  // left edge resizes it; the width is remembered beside the view preference.
  const [panelWidth, setPanelWidth] = usePanelWidth();
  const wide = useWide();
  const onResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    const startX = e.clientX;
    const startW = panelWidth;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => setPanelWidth(clampWidth(startW + (startX - ev.clientX)));
    const up = () => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  return (
    <aside
      aria-labelledby={headingId}
      // `nokey`: typing here never reaches the canvas's Delete, Escape or arrow keys.
      className="nokey np-panel fixed inset-0 z-50 flex flex-col overflow-y-auto bg-surface-container p-4 min-[760px]:absolute min-[760px]:inset-auto min-[760px]:bottom-3 min-[760px]:right-3 min-[760px]:top-3 min-[760px]:z-20 min-[760px]:rounded-xl"
      style={wide ? { width: panelWidth } : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !editing) {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      {wide && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          aria-valuenow={panelWidth}
          aria-valuemin={MIN_PANEL_WIDTH}
          aria-valuemax={MAX_PANEL_WIDTH}
          tabIndex={0}
          onPointerDown={onResizeStart}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); setPanelWidth(clampWidth(panelWidth + 24)); }
            if (e.key === 'ArrowRight') { e.preventDefault(); setPanelWidth(clampWidth(panelWidth - 24)); }
          }}
          className="absolute bottom-0 left-0 top-0 w-2 -translate-x-1 cursor-col-resize touch-none rounded-full hover:bg-surface-container-highest focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        />
      )}
      <div className="flex items-start gap-2">
        {node.color !== 'none' && (
          <span aria-hidden="true" className="mt-2 h-3 w-6 flex-shrink-0 rounded-full" style={{ backgroundColor: NODE_SWATCH[node.color] }} />
        )}
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="min-w-0 flex-1 pt-0.5 text-[17px] font-bold leading-snug text-on-surface break-words focus:outline-none"
        >
          {number && (
            <>
              <span className="mr-1.5 font-mono text-[0.85em] font-medium tabular-nums text-on-surface-variant">{number}</span>{' '}
            </>
          )}
          {node.title}
        </h2>
        {node.kind === 'group' && <span className="np-pill mt-1.5 flex-shrink-0">Group</span>}
        <Appear show={!!done} className="np-pill np-pill-icon mt-1.5 flex-shrink-0">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
            <path fill="currentColor" d={ICON_PATH.check} />
          </svg>
          Done
        </Appear>
        <Tooltip label="Close the topic panel">
          <IconButton label="Close topic panel" onClick={onClose} className="-mr-1 -mt-0.5">
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none">
              <path fill="currentColor" d="M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12 19 17.6 17.6 19 12 13.4 6.4 19 5 17.6 10.6 12 5 6.4z" />
            </svg>
          </IconButton>
        </Tooltip>
      </div>

      {parentTitle && (
        <Tooltip label="Open the group this sits in">
          <button
            type="button"
            onClick={onOpenParent}
            className={`${buttonClass('quiet', 'sm')} -ml-2 mt-1 max-w-full`}
          >
            <span className="truncate">In {parentTitle}</span>
          </button>
        </Tooltip>
      )}

      <Reveal show={!!(proposed || node.authority_name)}>
      {(proposed || node.authority_name) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {proposed && (
            <span className="inline-flex items-center rounded-full border border-dashed border-outline px-2.5 py-0.5 text-xs font-medium text-on-surface-variant">
              Proposed{node.proposed_by_name ? ` by ${node.proposed_by_name}` : ''}
            </span>
          )}
          <AuthorityChip name={node.authority_name} />
        </div>
      )}
      </Reveal>

      <div className="mt-3">
        <FadeIn key={editing ? 'edit' : node.body ? 'body' : 'none'}>
        {editing ? (
          <NodeEditor
            node={node}
            onCancel={() => setEditing(false)}
            onSave={async (patch) => {
              const ok = await onSave(patch);
              if (ok) setEditing(false);
              return ok;
            }}
          />
        ) : node.body ? (
          <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface-variant">{node.body}</p>
        ) : (
          <p className="text-[13px] italic text-on-surface-variant">No description yet.</p>
        )}
        </FadeIn>
      </div>

      {canEditNode && !editing && (
        <div role="group" aria-label="Topic color" className="mt-4 flex flex-wrap items-center gap-0.5">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={node.color === c}
              aria-label={COLOR_NAME[c]}
              disabled={busy}
              onClick={() => (c === node.color ? undefined : run(() => onSave({ color: c })))}
              className={`np-state flex h-8 w-9 items-center justify-center rounded-full ${MOTION} ${FOCUS}`}
            >
              <span
                aria-hidden="true"
                className={`block h-3 w-6 rounded-full ${c === 'none' ? 'bg-surface-container-highest' : ''} ${
                  node.color === c ? 'ring-2 ring-on-surface ring-offset-2 ring-offset-surface-container' : ''
                }`}
                style={c === 'none' ? undefined : { backgroundColor: NODE_SWATCH[c] }}
              />
            </button>
          ))}
        </div>
      )}

      {!editing && (canEditText || canEditNode || canDecide || canSetAuthority) && (
        <div className="mt-4 flex flex-wrap items-center gap-1">
          {proposed && canDecide && (
            <button type="button" disabled={busy} onClick={() => run(onAccept)} className={ACCEPT}>
              Accept
            </button>
          )}
          {canEditText && (
            <button ref={editTrigger} type="button" onClick={() => setEditing(true)} className={ROW_BUTTON}>
              Edit
            </button>
          )}
          {canSetAuthority && (
            <button
              ref={authorityTrigger}
              type="button"
              disabled={busy}
              aria-haspopup="dialog"
              aria-expanded={choosing}
              onClick={() => setChoosing((c) => !c)}
              className={ROW_BUTTON}
            >
              {node.authority_id ? 'Change authority' : 'Set authority'}
            </button>
          )}
          {proposed && canDecide && (
            <button type="button" disabled={busy} onClick={() => run(onReject)} className={DANGER_ROW_BUTTON}>
              Reject
            </button>
          )}
          {canMarkDone && (
            <button type="button" disabled={busy} onClick={() => run(onToggleDone)} className={ROW_BUTTON}>
              {done ? 'Reopen' : 'Mark as done'}
            </button>
          )}
          {!proposed && canEditNode && (
            <button type="button" disabled={busy} onClick={() => run(onDelete)} className={DANGER_ROW_BUTTON}>
              {node.kind === 'group' ? 'Remove group' : 'Remove topic'}
            </button>
          )}
        </div>
      )}

      {choosing && canSetAuthority && (
        <AuthorityPicker
          anchor={authorityTrigger}
          nodeTitle={node.title}
          members={members}
          currentId={node.authority_id}
          onPick={(userId) => {
            setChoosing(false);
            if (userId !== node.authority_id) run(() => onSetAuthority(userId));
          }}
          onClose={() => setChoosing(false)}
        />
      )}

      <div className="mt-5 border-t border-outline-variant pt-4">
        <PointsPanel
          key={node.id}
          planId={points.planId}
          nodeId={node.id}
          role={points.role}
          userId={points.userId}
          authorityId={node.authority_id}
          locked={points.locked}
          archived={points.archived}
          members={members}
          seq={node.seq}
          variant="panel"
          onChanged={points.onChanged}
        />
      </div>

      <div className="mt-auto pt-6">
        <button
          type="button"
          onClick={onOpenInDocument}
          className={buttonClass('quiet', 'sm')}
        >
          Open in document
        </button>
      </div>
    </aside>
  );
};

export default NodeSidePanel;
